import React, { useMemo, useState } from "react";
import { PiInfoFill } from "react-icons/pi";

export const getPasswordStrength = (password = "") => {
  if (!password) {
    return {
      score: 0,
      activeBars: 0,
      label: "",
      textColor: "text-gray-400",
      barColor: "bg-gray-200",
      isLengthValid: false,
    };
  }

  const isLengthValid = password.length >= 8;
  const hasLower = /[a-z]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);

  let criteriaCount = 0;
  if (hasLower && hasUpper) criteriaCount++;
  else if (hasLower || hasUpper) criteriaCount += 0.5;
  if (hasNumber) criteriaCount++;
  if (hasSpecial) criteriaCount++;

  let score = 1;
  if (!isLengthValid) {
    score = 1;
  } else if (criteriaCount >= 3) {
    score = 4;
  } else if (criteriaCount >= 2) {
    score = 3;
  } else if (criteriaCount >= 1) {
    score = 2;
  } else {
    score = 1;
  }

  switch (score) {
    case 4:
      return {
        score: 4,
        activeBars: 4,
        label: "Strong password",
        textColor: "text-[#10B981]",
        barColor: "bg-[#10B981]",
        isLengthValid,
      };
    case 3:
      return {
        score: 3,
        activeBars: 3,
        label: "Good password",
        textColor: "text-[#10B981]",
        barColor: "bg-[#10B981]",
        isLengthValid,
      };
    case 2:
      return {
        score: 2,
        activeBars: 2,
        label: "Fair password",
        textColor: "text-[#F59E0B]",
        barColor: "bg-[#F59E0B]",
        isLengthValid,
      };
    case 1:
    default:
      return {
        score: 1,
        activeBars: 1,
        label: "Weak password",
        textColor: "text-[#EF4444]",
        barColor: "bg-[#EF4444]",
        isLengthValid,
      };
  }
};

const PasswordStrengthIndicator = ({ password = "", className = "" }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { activeBars, label, textColor, barColor } = useMemo(
    () => getPasswordStrength(password),
    [password]
  );

  return (
    <div className={`w-full mt-1 mb-0.5 ${className}`}>
      <div className="flex items-center justify-between text-xs mb-1 relative">
        <span className={`font-bold transition-colors duration-200 ${label ? textColor : "text-transparent"}`}>
          {label || "Strength"}
        </span>

        {/* (i) Guidance Button & Light Theme Tooltip on the Right */}
        <div
          className="relative flex items-center"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <button
            type="button"
            onClick={() => setShowTooltip((prev) => !prev)}
            className="text-gray-800 hover:text-black focus:outline-none transition-colors cursor-pointer flex items-center justify-center p-0.5"
            aria-label="Password requirements"
            title="Password guidance"
          >
            <PiInfoFill className="w-4 h-4 text-[#111827] hover:text-black transition-colors" />
          </button>

          {/* Light Theme Info Tooltip */}
          {showTooltip && (
            <div className="absolute right-0 bottom-full mb-2 w-64 bg-white border border-gray-100 text-gray-800 text-xs rounded-2xl p-3.5 shadow-[0_10px_35px_rgba(0,0,0,0.12)] z-40 pointer-events-none transition-all duration-200 animate-in fade-in zoom-in-95">
              <div className="font-bold text-[#111827] mb-2 flex items-center gap-1.5 text-xs">
                <svg className="w-4 h-4 text-[#10B981]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Strong Password Guide</span>
              </div>
              <ul className="space-y-1.5 text-[11px] text-gray-600">
                <li className="flex items-center gap-1.5">
                  {password.length >= 8 ? (
                    <svg className="w-3.5 h-3.5 text-[#10B981] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0 ml-1 mr-1" />
                  )}
                  <span className={password.length >= 8 ? "text-[#10B981] font-semibold" : ""}>
                    At least 8 characters
                  </span>
                </li>
                <li className="flex items-center gap-1.5">
                  {/[a-z]/.test(password) && /[A-Z]/.test(password) ? (
                    <svg className="w-3.5 h-3.5 text-[#10B981] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0 ml-1 mr-1" />
                  )}
                  <span className={/[a-z]/.test(password) && /[A-Z]/.test(password) ? "text-[#10B981] font-semibold" : ""}>
                    Both uppercase & lowercase letters
                  </span>
                </li>
                <li className="flex items-center gap-1.5">
                  {/\d/.test(password) ? (
                    <svg className="w-3.5 h-3.5 text-[#10B981] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0 ml-1 mr-1" />
                  )}
                  <span className={/\d/.test(password) ? "text-[#10B981] font-semibold" : ""}>
                    At least one number (0-9)
                  </span>
                </li>
                <li className="flex items-center gap-1.5">
                  {/[^A-Za-z0-9]/.test(password) ? (
                    <svg className="w-3.5 h-3.5 text-[#10B981] shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0 ml-1 mr-1" />
                  )}
                  <span className={/[^A-Za-z0-9]/.test(password) ? "text-[#10B981] font-semibold" : ""}>
                    At least one special character (!@#$%)
                  </span>
                </li>
              </ul>
              {/* Pointer Arrow */}
              <div className="absolute right-1.5 top-full w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-white drop-shadow-sm" />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {[1, 2, 3, 4].map((index) => {
          const isActive = password && index <= activeBars;
          return (
            <div
              key={index}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                isActive ? barColor : "bg-gray-200"
              }`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
